// client/src/components/pages/EmailReports/ReportActiveSwitch.tsx (ST-00238)
//
// A report's on/off switch in the Email Reports list. On: the report is sent by its schedule
// and by Send Now. Off: nothing is sent until it is switched on again. One click, no
// confirmation: the page flips the row at once, calls PATCH /email-reports/:id/active and
// puts the row back if the call fails. This component only shows the state and reports the
// click.
//
// `readOnly` (no administrator role): the state is shown but the switch cannot be pressed.
// `busy` (a call in flight): clicks are ignored, but the switch keeps keyboard focus.
// Colours are theme tokens (primary / slate / surface re-theme in dark mode).

export interface ReportActiveSwitchProps {
  /** The report's name: the switch's accessible name is "Active: <name>". */
  reportName: string;
  checked: boolean;
  readOnly?: boolean;
  busy?: boolean;
  onChange: (on: boolean) => void;
}

export default function ReportActiveSwitch({
  reportName,
  checked,
  readOnly = false,
  busy = false,
  onChange,
}: ReportActiveSwitchProps) {
  const hint = readOnly
    ? 'Only an administrator can switch a report on or off'
    : checked
      ? 'On: click to stop sending this report'
      : 'Off: click to send this report again';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`Active: ${reportName}`}
      aria-busy={busy || undefined}
      aria-disabled={busy || undefined}
      disabled={readOnly}
      title={hint}
      onClick={() => {
        if (!readOnly && !busy) onChange(!checked);
      }}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
        checked ? 'bg-primary-500' : 'bg-slate-400'
      } ${readOnly ? 'cursor-not-allowed opacity-60' : busy ? 'cursor-wait' : 'cursor-pointer'}`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}
